import {
  EVENT_DAILY_REWARD_MODAL_CLOSED,
  EVENT_DAILY_REWARD_MODAL_OPENED,
  EVENT_OPEN_DAILY_REWARD_MODAL,
} from "@components/features/DailyRewardModal";
import { EVENT_CLOSE_DEPOSIT, EVENT_OPEN_DEPOSIT } from "@components/utils/DepositDrawer/constants";
import {
  EVENT_CLOSE_NOTIFICATION_DRAWER,
  EVENT_OPEN_NOTIFICATION_DRAWER,
} from "@components/utils/NotificationDrawer/constants";
import {
  EVENT_ONBOARDING_DIALOG_CLOSED,
  EVENT_OPEN_ONBOARDING_DIALOG,
  OnboardingDialogStep,
} from "@components/utils/OnboardingDialog/constants";
import { EVENT_CLOSE_WITHDRAW, EVENT_OPEN_WITHDRAW } from "@components/utils/WithdrawDrawer";
import { Paths } from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import useInIframe from "@hooks/useInIframe";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePostLoginInfo from "@hooks/usePostLoginInfo";
import usePromotionStatus from "@hooks/usePromotionStatus";
import { AuthStatus } from "@types";
import { isGameIframeDialogOpen } from "@utils/ui";
import {
  EVENT_CLOSE_REFER_BALLS_DRAWER,
  EVENT_OPEN_REFER_BALLS_DRAWER,
} from "@views/ReferBallsPage/components/ReferBallsDrawer/constants";
import { MemberSettingPlayerNameCollectionMode } from "kz-ui-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export interface UseDailyRewardModalTriggerResult {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  wasTriggeredFromPostLoginStep: boolean;
}

// Cooldown durations for route changes
const ROUTE_CHANGE_COOLDOWN_MS = 300; // Standard cooldown for general route changes
const DAILY_REWARD_PAGE_COOLDOWN_MS = 2000; // Longer cooldown when navigating from DailyRewardPage (matches RTK Query cache window)

/**
 * Hook to manage Daily Login Modal trigger logic
 * Checks eligibility, detects iframe/drawer flows, and manages modal state
 *
 * IMPORTANT: Modal displays once per page load/session when conditions are met.
 * It will show on initial page load, reload, or after login when eligible.
 */
export function useDailyRewardModalTrigger(): UseDailyRewardModalTriggerResult {
  const { shouldShowDailyLoginModal, isDailyBonusClaimable, isFetching } = usePromotionStatus();
  const { status: authStatus } = useAuthSession();
  const { isFulfilled: isPostLoginInfoFulfilled } = usePostLoginInfo();
  const { nameCollectionMode } = useMemberSettings();
  const isInIframe = useInIframe();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDepositDrawerOpen, setIsDepositDrawerOpen] = useState(false);
  const [isWithdrawDrawerOpen, setIsWithdrawDrawerOpen] = useState(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [isReferBallsDrawerOpen, setIsReferBallsDrawerOpen] = useState(false);
  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useState(false);
  const [pendingOpenRequest, setPendingOpenRequest] = useState(false);
  const hasTriggeredRef = useRef(false);
  const prevAuthStatusRef = useRef<AuthStatus | undefined>(undefined);
  const currentOnboardingStepRef = useRef<OnboardingDialogStep | undefined>(undefined);
  const [wasTriggeredFromPostLoginStep, setWasTriggeredFromPostLoginStep] = useState(false);
  const prevIsModalOpenRef = useRef(isModalOpen);
  const prevPathnameRef = useRef(location.pathname);
  const [isRouteChangeCooldown, setIsRouteChangeCooldown] = useState(false);

  // Track DepositDrawer state via events
  useEffect(() => {
    const handleOpenDeposit = () => {
      setIsDepositDrawerOpen(true);
    };

    const handleCloseDeposit = () => {
      setIsDepositDrawerOpen(false);
    };

    window.addEventListener(EVENT_OPEN_DEPOSIT, handleOpenDeposit);
    window.addEventListener(EVENT_CLOSE_DEPOSIT, handleCloseDeposit);

    return () => {
      window.removeEventListener(EVENT_OPEN_DEPOSIT, handleOpenDeposit);
      window.removeEventListener(EVENT_CLOSE_DEPOSIT, handleCloseDeposit);
    };
  }, []);

  // Track WithdrawDrawer state via events
  useEffect(() => {
    const handleOpenWithdraw = () => {
      setIsWithdrawDrawerOpen(true);
    };

    const handleCloseWithdraw = () => {
      setIsWithdrawDrawerOpen(false);
    };

    window.addEventListener(EVENT_OPEN_WITHDRAW, handleOpenWithdraw);
    window.addEventListener(EVENT_CLOSE_WITHDRAW, handleCloseWithdraw);

    return () => {
      window.removeEventListener(EVENT_OPEN_WITHDRAW, handleOpenWithdraw);
      window.removeEventListener(EVENT_CLOSE_WITHDRAW, handleCloseWithdraw);
    };
  }, []);

  // Track NotificationDrawer state via events
  useEffect(() => {
    const handleOpenNotification = () => {
      setIsNotificationDrawerOpen(true);
    };

    const handleCloseNotification = () => {
      setIsNotificationDrawerOpen(false);
    };

    window.addEventListener(EVENT_OPEN_NOTIFICATION_DRAWER, handleOpenNotification);
    window.addEventListener(EVENT_CLOSE_NOTIFICATION_DRAWER, handleCloseNotification);

    return () => {
      window.removeEventListener(EVENT_OPEN_NOTIFICATION_DRAWER, handleOpenNotification);
      window.removeEventListener(EVENT_CLOSE_NOTIFICATION_DRAWER, handleCloseNotification);
    };
  }, []);

  // Track ReferBallsDrawer state via events
  useEffect(() => {
    const handleOpenReferBalls = () => {
      setIsReferBallsDrawerOpen(true);
    };

    const handleCloseReferBalls = () => {
      setIsReferBallsDrawerOpen(false);
    };

    window.addEventListener(EVENT_OPEN_REFER_BALLS_DRAWER, handleOpenReferBalls);
    window.addEventListener(EVENT_CLOSE_REFER_BALLS_DRAWER, handleCloseReferBalls);

    return () => {
      window.removeEventListener(EVENT_OPEN_REFER_BALLS_DRAWER, handleOpenReferBalls);
      window.removeEventListener(EVENT_CLOSE_REFER_BALLS_DRAWER, handleCloseReferBalls);
    };
  }, []);

  // Helper to check if a step is a post-login info step
  const isPostLoginStep = (step?: OnboardingDialogStep) => {
    if (!step) return false;
    return [
      OnboardingDialogStep.AddBankAccount,
      OnboardingDialogStep.AddLegalName,
      OnboardingDialogStep.AddBankLegalName,
      OnboardingDialogStep.UpdateBankAccount,
      OnboardingDialogStep.UpdateLegalName,
      OnboardingDialogStep.UpdateBankLegalName,
    ].includes(step);
  };

  // Track OnboardingDialog state via events
  useEffect(() => {
    const handleOpenOnboarding = (event: Event) => {
      const customEvent = event as CustomEvent<{ step?: OnboardingDialogStep }>;
      const step = customEvent.detail?.step;
      currentOnboardingStepRef.current = step;
      setIsOnboardingDialogOpen(true);
    };

    const handleCloseOnboarding = (event: Event) => {
      const customEvent = event as CustomEvent<{ step?: OnboardingDialogStep }>;
      const closedStep = customEvent.detail?.step;
      const wasPostLoginStep = isPostLoginStep(closedStep);

      // If we just closed a post-login info step, trigger modal
      // Conditions will determine if it should actually show
      if (wasPostLoginStep) {
        setWasTriggeredFromPostLoginStep(true);
        setPendingOpenRequest(true);
      }

      currentOnboardingStepRef.current = undefined;
      setIsOnboardingDialogOpen(false);
    };

    window.addEventListener(EVENT_OPEN_ONBOARDING_DIALOG, handleOpenOnboarding);
    window.addEventListener(EVENT_ONBOARDING_DIALOG_CLOSED, handleCloseOnboarding);

    return () => {
      window.removeEventListener(EVENT_OPEN_ONBOARDING_DIALOG, handleOpenOnboarding);
      window.removeEventListener(EVENT_ONBOARDING_DIALOG_CLOSED, handleCloseOnboarding);
    };
  }, []);

  // Listen for explicit trigger events from post-login handlers
  // Set a flag to open modal once conditions are met
  useEffect(() => {
    const handleOpenModal = () => {
      // Set flag to indicate we want to open the modal
      setPendingOpenRequest(true);
    };

    window.addEventListener(EVENT_OPEN_DAILY_REWARD_MODAL, handleOpenModal);

    return () => {
      window.removeEventListener(EVENT_OPEN_DAILY_REWARD_MODAL, handleOpenModal);
    };
  }, []);

  // Reset trigger flag when user logs out
  // This allows the modal to show once again after the next login/registration
  useEffect(() => {
    const wasAuthenticated = prevAuthStatusRef.current === AuthStatus.Authenticated;
    const isNowUnauthenticated = authStatus === AuthStatus.Unauthenticated || authStatus === AuthStatus.Authenticating;

    if (wasAuthenticated && isNowUnauthenticated) {
      hasTriggeredRef.current = false;
      setPendingOpenRequest(false);
    }

    prevAuthStatusRef.current = authStatus;
  }, [authStatus]);

  // Track route changes and set cooldown to prevent race condition
  // When navigating back from claim pages, RTK Query might serve cached data
  // briefly before starting to refetch. This cooldown prevents the modal from opening
  // during that brief window.
  useEffect(() => {
    const pathnameChanged = prevPathnameRef.current !== location.pathname;

    if (pathnameChanged) {
      const prevPathname = prevPathnameRef.current;

      // Determine cooldown duration based on previous page
      // DailyRewardPage requires longer delay to match RTK Query cache window (2s)
      // Other pages use shorter delay for general race condition prevention (300ms)
      const isFromDailyRewardPage = prevPathname === Paths.PRIVATE.PROMOTION_DAILY_LOGIN;
      const cooldownDuration = isFromDailyRewardPage ? DAILY_REWARD_PAGE_COOLDOWN_MS : ROUTE_CHANGE_COOLDOWN_MS;

      // Set cooldown flag to block modal opening immediately after route change
      setIsRouteChangeCooldown(true);

      // Clear cooldown after delay to allow RTK Query to start refetching
      const cooldownTimer = setTimeout(() => {
        setIsRouteChangeCooldown(false);
      }, cooldownDuration);

      prevPathnameRef.current = location.pathname;

      return () => {
        clearTimeout(cooldownTimer);
      };
    }
  }, [location.pathname]);

  // Check if conditions are met to show modal (excluding hasTriggeredRef check)
  const areConditionsMet = useMemo(() => {
    // Only show if user is authenticated
    if (authStatus !== AuthStatus.Authenticated) {
      return false;
    }

    // Don't show if backend flag is not set
    if (!shouldShowDailyLoginModal) {
      return false;
    }

    // Don't show if reward status is being fetched (wait for fresh data)
    if (isFetching) {
      return false;
    }

    // Don't show if reward is not actually claimable
    if (!isDailyBonusClaimable) {
      return false;
    }

    // Don't show in iframe flows
    if (isInIframe) {
      return false;
    }

    // Don't show if game iframe dialog is open
    if (isGameIframeDialogOpen()) {
      return false;
    }

    // Don't show if any drawer is open
    if (isDepositDrawerOpen || isWithdrawDrawerOpen || isNotificationDrawerOpen || isReferBallsDrawerOpen) {
      return false;
    }

    // Don't show if onboarding dialog is open
    if (isOnboardingDialogOpen) {
      return false;
    }

    // Don't show if user is on the daily login page
    if (location.pathname === Paths.PRIVATE.PROMOTION_DAILY_LOGIN) {
      return false;
    }

    // Don't show if we're in route change cooldown period
    // This prevents race condition where modal opens before RTK Query starts refetching
    if (isRouteChangeCooldown) {
      return false;
    }

    return true;
  }, [
    authStatus,
    shouldShowDailyLoginModal,
    isFetching,
    isDailyBonusClaimable,
    isInIframe,
    isDepositDrawerOpen,
    isWithdrawDrawerOpen,
    isNotificationDrawerOpen,
    isReferBallsDrawerOpen,
    isOnboardingDialogOpen,
    location.pathname,
    isRouteChangeCooldown,
  ]);

  // Auto-trigger and open modal when conditions are met
  // - On page load/reload: triggers if post-login info is fulfilled (prevents race condition)
  // - After post-login dialog closes: opens even if post-login info not fulfilled (dialog was already shown)
  // - From explicit events: opens when pending request exists
  // Only check postLogin info fulfillment if nameCollectionMode is Registration, else ignore it
  const shouldCheckPostLoginInfo = nameCollectionMode === MemberSettingPlayerNameCollectionMode.Registration;
  const isPostLoginInfoSatisfied = shouldCheckPostLoginInfo ? isPostLoginInfoFulfilled : true;

  useEffect(() => {
    if (!areConditionsMet || hasTriggeredRef.current || isModalOpen) {
      return;
    }

    // Case 1: Auto-trigger on page load (no pending request yet, post-login info fulfilled)
    if (!pendingOpenRequest && isPostLoginInfoSatisfied) {
      setPendingOpenRequest(true);
      return;
    }

    // Case 2: Open from pending request (either post-login fulfilled OR triggered from post-login step)
    if (pendingOpenRequest && (isPostLoginInfoSatisfied || wasTriggeredFromPostLoginStep)) {
      setIsModalOpen(true);
      hasTriggeredRef.current = true;
      setPendingOpenRequest(false);
    }
  }, [areConditionsMet, isModalOpen, pendingOpenRequest, isPostLoginInfoSatisfied, wasTriggeredFromPostLoginStep]);

  // Dispatch state change events when modal opens/closes
  useEffect(() => {
    const wasOpen = prevIsModalOpenRef.current;
    const isNowOpen = isModalOpen;

    if (!wasOpen && isNowOpen) {
      window.dispatchEvent(new Event(EVENT_DAILY_REWARD_MODAL_OPENED));
    } else if (wasOpen && !isNowOpen) {
      window.dispatchEvent(new Event(EVENT_DAILY_REWARD_MODAL_CLOSED));
    }

    prevIsModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  // Reset post-login step trigger flag when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      setWasTriggeredFromPostLoginStep(false);
    }
  }, [isModalOpen]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    hasTriggeredRef.current = false;
  }, []);

  // Close modal if user navigates to daily login page
  useEffect(() => {
    if (isModalOpen && location.pathname === Paths.PRIVATE.PROMOTION_DAILY_LOGIN) {
      closeModal();
    }
  }, [location.pathname, isModalOpen, closeModal]);

  const openModal = useCallback(() => {
    if (areConditionsMet && !hasTriggeredRef.current) {
      setIsModalOpen(true);
      hasTriggeredRef.current = true;
      setPendingOpenRequest(false);
    } else {
      // If conditions not met, set pending flag
      setPendingOpenRequest(true);
    }
  }, [areConditionsMet]);

  return {
    isModalOpen,
    openModal,
    closeModal,
    wasTriggeredFromPostLoginStep,
  };
}
